import { useContext, useMemo, useRef } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { EditRecordingForm, type ExposedFormMethods, type FormData } from './EditRecordingForm';
import { ClassContext } from '../../context/ClassContext';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { batchUpdateRecordings, type BatchUpdateRecordingsData } from '../../requests/batchUpdateRecordings';
import {
    type ClassRecording,
    type Permission,
    type RecordingBulkPatchItem,
    type RecordingRestrictMode
} from '../classroom/types';
import { type RecordingDialogProps } from '../classroom/ui-types';
import { ConfirmationDialog } from '../common/dialog/ConfirmationDialog';

export type EditRecordingDialogProps = RecordingDialogProps;

export const EditRecordingDialog = (props: EditRecordingDialogProps) => {
    const { t } = useTranslation();
    const { open, recording, recordings, setRecordings, onClose } = props;
    const { enqueueSnackbar } = useSnackbar();
    const { data: schoolInfo } = useSchoolInfo();
    const schoolUuid = schoolInfo?.uuid;
    const editFormRef = useRef<ExposedFormMethods>(null);

    const { userUuid, classUuid } = useContext(ClassContext);

    const handleClose = () => {
        onClose?.({}, 'backdropClick');
    };

    const mutation = useMutation({
        mutationFn: async (data: BatchUpdateRecordingsData) => batchUpdateRecordings(data),
        onSuccess: () => {
            handleClose();
            enqueueSnackbar(t('lti:recordings.update-success'), {
                variant: 'success'
            });
        },
        onError: () => {
            enqueueSnackbar(t('lti:recordings.update-failed'), {
                variant: 'error'
            });
        }
    });

    const onSubmitFormValid = async (data: FormData) => {
        if (!recording?.recordingId || !schoolUuid || !classUuid || !userUuid) {
            console.error('Missing required data.');
            return;
        }

        // Update context to reflect changes.
        const updatedRecordings = recordings.map((r) => {
            if (r.recordingId === recording?.recordingId) {
                return { ...r, ...data, permission: data.permission as Permission };
            }
            return r;
        });
        setRecordings(updatedRecordings);

        const recordingPatchItem: RecordingBulkPatchItem = {
            recordingId: recording?.recordingId,
            name: data.name,
            permission: data.permission as Permission,
            restrictBy: data.restrictBy as RecordingRestrictMode,
            studentGroups: data.studentGroups
        };

        const patchData: BatchUpdateRecordingsData = { userUuid, classUuid, recordings: [recordingPatchItem] };
        await mutation.mutateAsync(patchData);
        handleClose();
    };

    const handleDialogConfirmed = () => {
        editFormRef?.current?.triggerSubmit();
    };

    const { isLoading: confirming } = mutation;

    const defaultFormData: FormData | undefined = useMemo(() => {
        if (!recording) {
            return undefined;
        }
        const { name, permission, restrictions } = recording as ClassRecording;
        const studentGroups = restrictions?.lms_groups.map((g) => g.studentGroupId);
        return {
            name: name ?? '',
            permission,
            restrictBy: restrictions?.mode ?? '',
            studentGroups: studentGroups ?? []
        };
    }, [recording]);

    return (
        <ConfirmationDialog
            id="edit-recording"
            open={open}
            onClose={handleClose}
            title={t('lti:recordings.edit-title', { name: recording?.name ?? '' })}
            confirmLabel={t('common:save')}
            onConfirm={handleDialogConfirmed}
            confirming={confirming}
        >
            <EditRecordingForm
                ref={editFormRef}
                onSubmitValid={onSubmitFormValid}
                showNameField={true}
                defaultFormData={defaultFormData}
                dialogOpen={open}
            />
        </ConfirmationDialog>
    );
};
